import * as React from "react";
import { Editor as TinyMCEEditor, EditorEvent, RawEditorOptions, TinyMCE } from "tinymce";

import { ScriptLoader } from "../ScriptLoader";
import { getTinymce } from "../TinyMCE";
import { configHandlers, isFunction, isTextareaOrInput, mergePlugins, uuid } from "../Utils";
import { IRichTextEditorWrapperProps, REditorCSS, REditorSkin } from "./types";

const changeEvents = "change input compositionend setcontent";

//hodnoty možných stavů nastavení výchozí hodnoty
enum ValueResetState {
    ResetNeeded,
    DoneWithUndefinedValue,
    Done,
}

export class RichTextEditorWrapperComponent extends React.Component<IRichTextEditorWrapperProps> {
    public static defaultProps: Partial<IRichTextEditorWrapperProps> = {
        // Tohle je cloudChannel pro richTextEdit a symbolizuje, ktera verze richTextEditu se bude stahovat, jestli
        // Stable nebo Dev. Pro nas to nema zadny vyznam, protoze my to mame hostovane u nas, ale bez toho
        // to tu v nekterych mistech padalo.
        cloudChannel: "5",
    };

    public editor?: TinyMCEEditor;

    private readonly _id: string;
    private readonly _elementRef: React.RefObject<HTMLElement>;
    private readonly _inline: boolean;
    private _currentContent?: string;
    private _boundHandlers: Record<string, (event: EditorEvent<unknown>) => unknown>;
    private _needValueReset: ValueResetState = ValueResetState.DoneWithUndefinedValue; //příznak identifikující stav nastavení výchozí hodnoty
    private _finalInit: RawEditorOptions;
    private _tinymce?: TinyMCE;
    private _darkTheme: boolean;
    private _editorWaitingForFocus: boolean;

    constructor(props: Partial<IRichTextEditorWrapperProps>) {
        super(props);
        this._id = this.props.id || uuid("tiny-react");
        this._elementRef = React.createRef<HTMLElement>();
        this._inline = this.props.inline ?? this.props.init?.inline ?? false;
        this._boundHandlers = {};
        this._finalInit = {};
        this._darkTheme = !!this.props.darkTheme;
        this._editorWaitingForFocus = false;
    }

    public override componentDidUpdate(prevProps: Partial<IRichTextEditorWrapperProps>): void {
        if (this.editor) {
            this._bindHandlers(prevProps);
            if (this.editor.initialized) {
                if (this.props.isValid !== undefined) {
                    this.editor?.getContainer().classList.toggle("invalid", this.props.isValid);
                }
                this._currentContent = this._currentContent ?? this.editor.getContent();
                //console.log(`RTE Update ${this._id}-${this.props.dataKey}/${prevProps.dataKey} initialValue:${this.props.initialValue}/${prevProps.initialValue}, value:${this.props.value}/${prevProps.value}, needValueReset:${this._needValueReset}, isDirty:${this.editor.isDirty()}`);
                /*Nastavení výchozí hodnoty - princip inicializace výchozí hodnoty spočívá v tom, že při změně datakey se nastaví příznak,
                že je potřeba provést inicializaci. Inicializace se hned provede i když ještě není nastavena initialValue (při zakládání je to finální
                stav inicializace. Pokud dojde ještě později k změně initialValue a uživatel ještě neprovedl změnu, pak se provede inicializace
                ještě jednou ,už se skutečnou initialValue.*/
                let wasResetDone = false;//pomocný flag indikující, zda v tomto průchodu došlo k reset výchozí hodnoty
                if (this.props.dataKey !== prevProps.dataKey) {
                    //pokud dojde ke změně dataKey je vyžadován reset vychozí hodnoty
                    // console.log(`RTE ${this._id}-${this.props.dataKey} need value reset`);
                    this._needValueReset = ValueResetState.ResetNeeded;
                } else if (this._needValueReset < ValueResetState.Done && this.editor.isDirty()) {
                    //pokud je vyžadován reset výchozí hodnoty, ale již došlo k editaci tak se požadavek na reset zruší
                    // console.log(`RTE ${this._id}-${this.props.dataKey} value reset cancel because dirty`);
                    this._needValueReset = ValueResetState.Done;
                }
                //pokud je vyžadován provede se reset výchozí hodnoty a to buď hodnotou undefined nebo nějakou skutečnou hodnotou
                if (this._needValueReset < ValueResetState.DoneWithUndefinedValue || (this._needValueReset < ValueResetState.Done && this.props.initialValue)) {
                    if (this.props.initialValue) {
                        this._needValueReset = ValueResetState.Done;
                    } else {
                        //Byl proveden reset výchozí hodnoty, ale pouze na hodnotu undefined. Při zakládání záznamu je toto finální stav.
                        this._needValueReset = ValueResetState.DoneWithUndefinedValue;
                    }
                    const initialValue = this._getInitialValue();
                    // console.log(`RTE ${this._id}-${this.props.dataKey} reset with value ${initialValue}`);
                    // same as resetContent in TinyMCE 5
                    this.editor.setContent(initialValue, { no_events: true });
                    this.editor.undoManager.clear();
                    this.editor.undoManager.add();
                    this.editor.setDirty(false);
                    wasResetDone = true;
                }

                //tento kód je pro synchronizaci změn pokud mám více komponent RTE referencovaných na stejný atribut OWObject
                if (!wasResetDone && this.props.value !== prevProps.value && this.props.value !== this._currentContent) {
                    // console.log(`RTE ${this._id}-${this.props.dataKey} sync content with value ${this.props.value}`);
                    const newContentValue = this.props.value ?? "";
                    this.editor.setContent(newContentValue.toString(), { no_events: true });
                    this._currentContent = newContentValue;
                }

                if (this.props.disabled !== prevProps.disabled) {
                    const disabled = this.props.disabled ?? false;
                    this.editor.mode.set(disabled ? "readonly" : "design");

                    // Odstranění stávajícího editoru a inicializace nového se správným zobrazením/skrytím menubaru a toolbaru na základě this.props.disabled
                    this.editor.remove();
                    this._initialize();
                }
                if (this.props.darkTheme !== this._darkTheme) {
                    this._darkTheme = !this._darkTheme;
                    this.editor?.remove();
                    this._finalInit.skin = this.props.darkTheme ? REditorSkin.dark : REditorSkin.light;
                    this._finalInit.content_css = this.props.darkTheme ? REditorCSS.dark : REditorCSS.light;
                    this._tinymce?.init(this._finalInit);
                }
            }
        }
    }

    public override componentDidMount(): void {
        if (getTinymce() !== null) {
            this._initialize();
        } else if (this._elementRef.current && this._elementRef.current.ownerDocument) {
            ScriptLoader.load(
                this._elementRef.current.ownerDocument,
                this._getScriptSrc(),
                this.props.scriptLoading?.async ?? false,
                this.props.scriptLoading?.defer ?? false,
                this.props.scriptLoading?.delay ?? 0,
                this._initialize,
            );
        }
    }

    public override componentWillUnmount(): void {
        try {
            const editor = this.editor;
            if (editor) {
                editor.off(changeEvents, this._handleEditorChange);
                editor.off("keypress", this._handleEditorChangeSpecial);
                editor.off("NewBlock", this._handleEditorChange);
                Object.keys(this._boundHandlers).forEach((eventName) => {
                    editor.off(eventName, this._boundHandlers[eventName]);
                });
                this._boundHandlers = {};
                editor.remove();
                this.editor = undefined;
            }
        } catch (e) {
            // ignored
        }
    }

    public override render(): React.ReactElement<{ ref: React.RefObject<HTMLElement>;id: string }, string | React.JSXElementConstructor<any>> {
        return this._inline ? this._renderInline() : this._renderIframe();
    }

    private _renderInline(): React.ReactElement<{ ref: React.RefObject<HTMLElement>;id: string }, string | React.JSXElementConstructor<any>> {
        const { tagName = "div" } = this.props;

        return React.createElement(tagName, {
            ref: this._elementRef,
            id: this._id,
        });
    }

    private _renderIframe(): React.DetailedReactHTMLElement<{ ref: React.RefObject<HTMLElement>;style: { visibility: "hidden" };name: string | undefined;id: string }, HTMLElement> {
        return React.createElement("textarea", {
            ref: this._elementRef,
            style: { visibility: "hidden" },
            name: this.props.textareaName,
            id: this._id,
        });
    }

    private _getScriptSrc(): string {
        if (typeof this.props.tinymceScriptSrc === "string") {
            return this.props.tinymceScriptSrc;
        } else {
            return "modules/tinymce/tinymce.js";
        }
    }

    private _getInitialValue(): string {
        /*Hodnota initialValue se v redux nenastavuje zcela konzistentě. Po uložení nového záznamu dojde k naplnění initialValue novou hodnotou.
        Po uložení již existujícího záznamu zůstavá v initalValue původní hodnota. Podle mého názoru je to chyba, ale řešení nebude úplně triviální
        a mělo by se to asi řešit samostatně.
        Tady jsem se s tímto faktem vyrovnal tak, že pokud je nastaveno initialValue i value tak má přednost value.
        Není to úplně čisté řešení je potřeba brát v podtaz v jakém kontextu se volání metody _getInitialValue provádí.*/
        const initialValue = this.props.value && this.props.initialValue !== this.props.value ? this.props.value : this.props.initialValue;
        return initialValue?.toString() ?? "";
    }

    private _bindHandlers(prevProps: Partial<IRichTextEditorWrapperProps>): void {
        if (this.editor !== undefined) {
            // typescript chokes trying to understand the type of the lookup function
            configHandlers(this.editor, prevProps, this.props, this._boundHandlers, (key) => this.props[key] as any);
            // check if we should monitor editor changes
            const isValueControlled = (p: Partial<IRichTextEditorWrapperProps>) => p.onEditorChange !== undefined || p.value !== undefined;
            const wasControlled = isValueControlled(prevProps);
            const nowControlled = isValueControlled(this.props);
            if (!wasControlled && nowControlled) {
                this.editor.on(changeEvents, this._handleEditorChange);
                this.editor.on("keyup", this._handleEditorChangeSpecial);
                this.editor.on("NewBlock", this._handleEditorChange);
            } else if (wasControlled && !nowControlled) {
                this.editor.off(changeEvents, this._handleEditorChange);
                this.editor.off("keyup", this._handleEditorChangeSpecial);
                this.editor.off("NewBlock", this._handleEditorChange);
            }
        }
    }

    private _handleEditorChange = (_evt: EditorEvent<unknown>) => {
        const editor = this.editor;
        if (editor && editor.initialized) {
            const newContent = editor.getContent();

            if (newContent !== this._currentContent) {
                this._currentContent = newContent;
                if (isFunction(this.props.onEditorChange)) {
                    const format = this.props.outputFormat;
                    const out = format === "html" ? newContent : editor.getContent({ format });
                    this.props.onEditorChange(out, editor);
                }
            }
        }
    };

    private _handleEditorChangeSpecial = (evt: EditorEvent<KeyboardEvent>) => {
        if (evt.key === "Backspace" || evt.key === "Delete") {
            this._handleEditorChange(evt);
        }
    };

    private _initialize = () => {
        const target = this._elementRef.current;
        if (!target) {
            return; // Editor has been unmounted
        }

        const tinymce = getTinymce();
        if (!tinymce) {
            throw new Error("tinymce should have been loaded into global scope");
        } else {
            this._tinymce = tinymce;
        }

        this._finalInit = {
            ...this.props.init,
            selector: undefined,
            target,
            readonly: this.props.disabled,
            inline: this._inline,
            plugins: mergePlugins(this.props.init && this.props.init.plugins, this.props.plugins),
            toolbar: this.props.disabled ? !this.props.disabled : (this.props.toolbar || (this.props.init && this.props.init.toolbar)),
            menubar: this.props.init?.menubar && !this.props.disabled,
            skin: this.props.darkTheme ? REditorSkin.dark : REditorSkin.light,
            content_css: this.props.darkTheme ? REditorCSS.dark : REditorCSS.light,
            setup: (editor) => {
                this.editor = editor;
                this._bindHandlers({});

                // When running in inline mode the editor gets the initial value
                // from the innerHTML of the element it is initialized on.
                // However, we don't want to take on the responsibility of sanitizing
                // to remove XSS in the React integration, so we have a chicken and egg
                // problem... We avoid it by sneaking in a set content before the first
                // "official" setContent and using TinyMCE to do the sanitization.
                if (this._inline && !isTextareaOrInput(target)) {
                    editor.once("PostRender", (_evt) => {
                        const initialValue = this._getInitialValue();
                        editor.setContent(initialValue, { no_events: true });
                    });
                }

                if (this.props.init && isFunction(this.props.init.setup)) {
                    this.props.init.setup(editor);
                }
            },
            init_instance_callback: (editor) => {
                // check for changes that happened since tinymce.init() was called
                const initialValue = this._getInitialValue();
                this._currentContent = this._currentContent ?? editor.getContent();
                if (this._currentContent !== initialValue) {
                    this._currentContent = initialValue;
                    // same as resetContent in TinyMCE 5
                    editor.setContent(initialValue);
                    editor.undoManager.clear();
                    editor.undoManager.add();
                    editor.setDirty(false);
                }
                const disabled = this.props.disabled ?? false;
                editor.mode.set(disabled ? "readonly" : "design");

                const innerDocRoot = editor.contentDocument.documentElement;
                innerDocRoot.style.height = "100%";
                innerDocRoot.onclick = () => target.click(); // ruční zpropagování eventu, klik do iframu totiž nezapne editaci
                target.onclick = () => {
                    if (!editor.hasFocus()) {
                        this._focusAndSetCursorToEnd(editor); // ruční fokus kvůli prokliku z labelu
                        this._editorWaitingForFocus = true; // přechod do editace vytváří novou instanci editoru, ta dostane díky příznaku fokus v inicializaci
                    }
                };
                if (!disabled && this._editorWaitingForFocus) {
                    this._focusAndSetCursorToEnd(editor);
                    this._editorWaitingForFocus = false;
                }

                // ensure existing init_instance_callback is called
                if (this.props.init && isFunction(this.props.init.init_instance_callback)) {
                    this.props.init.init_instance_callback(editor);
                }
            },
        };
        if (!this._inline) {
            target.style.visibility = "";
        }
        if (isTextareaOrInput(target)) {
            target.value = this._getInitialValue();
        }

        this._tinymce.init(this._finalInit);
    };

    private _focusAndSetCursorToEnd = (editor: TinyMCEEditor) => {
        editor.focus();
        editor.selection.select(editor.getBody(), true);
        editor.selection.collapse(false);
    };
}
